import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, fetchAllocChangeFileUrl, type AllocChangeRow, type AllocSnap } from '../lib/api';
import Modal from './Modal';

/**
 * 운행편성 변경 알림(2026-10-02 way) — 구글폼 「차량변경정보공유」의 메일 공지를 대신한다.
 *
 *  · 회계팀(회계 화면 입력) = **팝업**. 「확인했습니다」를 누를 때까지 다시 뜬다(「나중에」는 30분).
 *  · 정산 담당(정산 계열·기준정보 입력) = 메뉴 배지 + 정산 화면 위 강조 띠(`AllocChangeBanner`).
 *  대상 = 지난달 이후 정산월의 신규·변경·종료·삭제 중 **내가 아직 확인 안 한 것**(내가 등록한 것 제외) — 서버 `allocChangePulse`.
 */

const POLL_MS = 60_000;
const SNOOZE_KEY = 'alloc-change-snooze-until';

// 메뉴 배지가 읽는 숫자 — Shell 이 다시 그려지지 않게 작은 저장소로 따로 둔다(issues 154: 배지 갱신이 본문 전체를 다시 그렸다).
let count = 0;
/** 배지 건이 어느 정산월에 있나(11-04 way 캡처 「확인할 게 없는데 왜 4」 — 배지는 지난달~두 달 뒤, 화면은 고른 달). [달, 건수] 오름차순. */
let mons: [string, number][] = [];
const subs = new Set<() => void>();
function setCount(n: number, items: { settle_mon: string }[]) {
  const m = new Map<string, number>();
  for (const i of items) m.set(i.settle_mon, (m.get(i.settle_mon) ?? 0) + 1);
  const next = [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const same = n === count && next.length === mons.length && next.every(([k, v], i) => mons[i]![0] === k && mons[i]![1] === v);
  if (same) return;
  count = n;
  mons = next;
  subs.forEach((f) => f());
}
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; };
export function useAllocChangeCount(): number {
  return useSyncExternalStore(subscribe, () => count);
}
export function useAllocChangeMons(): [string, number][] {
  return useSyncExternalStore(subscribe, () => mons);
}
/** 확인을 누른 화면이 맥박을 바로 다시 돌리게. */
export function pokeAllocChanges() {
  window.dispatchEvent(new Event('allocchange:poke'));
}

export function snapLine(s: AllocSnap | null | undefined): string {
  if (!s) return '—';
  return [s.car_no, s.owner_name ? `차주 ${s.owner_name}` : null, s.grp_name, s.driver_name ? `기사 ${s.driver_name}` : null]
    .filter(Boolean).join(' · ') || `편성 ${s.car_alloc_id}`;
}

export function changeText(r: AllocChangeRow): string {
  if (r.kind === '변경') return `${snapLine(r.old_snap)} → ${snapLine(r.new_snap)}`;
  if (r.kind === '신규') return snapLine(r.new_snap);
  return snapLine(r.old_snap ?? r.new_snap);
}

export function changeWhen(r: AllocChangeRow): string {
  if (r.kind === '변경') return `${r.apply_date}부터 새 편성`;
  if (r.kind === '종료') return `${r.apply_date}까지`;
  if (r.kind === '신규') return `${r.apply_date}부터`;
  return r.apply_date;
}

export const KIND_TONE: Record<string, string> = {
  변경: 'bg-amber-100 text-amber-800',
  종료: 'bg-red-100 text-red-700',
  신규: 'bg-emerald-100 text-emerald-800',
  삭제: 'bg-red-100 text-red-700',
  수정: 'bg-canvas text-ink-soft',
};

export function KindBadge({ kind }: { kind: string }) {
  return <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold ${KIND_TONE[kind] ?? 'bg-canvas'}`}>{kind}</span>;
}

function snoozed(): boolean {
  try { return Number(sessionStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now(); } catch { return false; }
}
function snooze() {
  try { sessionStorage.setItem(SNOOZE_KEY, String(Date.now() + 30 * 60_000)); } catch { /* 못 쓰면 다음 맥박에 다시 뜬다 */ }
}

export function AllocChangeAlerts() {
  const navigate = useNavigate();
  const [pop, setPop] = useState<AllocChangeRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [sales, setSales] = useState(false);
  const seenSig = useRef('');

  const tick = useCallback(() => {
    api.allocChangePulse().then((p) => {
      setCount(p.count, p.items);
      if (!p.modal || !p.items.length) { setPop(null); seenSig.current = ''; return; }
      const sig = p.items.map((i) => i.alloc_change_id).join(',');
      // 새 건이 오면 「나중에」를 풀고 다시 띄운다. 같은 목록이면 30분 뒤에 다시.
      if (sig !== seenSig.current) {
        try { sessionStorage.removeItem(SNOOZE_KEY); } catch { /* 무시 */ }
        seenSig.current = sig;
      }
      setSales(p.audience === 'sales');
      if (!snoozed()) setPop(p.items);
    }).catch(() => { /* 알림은 없어도 업무 화면은 그대로 */ });
  }, []);

  useEffect(() => {
    tick();
    const t = setInterval(() => { if (document.visibilityState === 'visible') tick(); }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    window.addEventListener('focus', tick);
    window.addEventListener('allocchange:poke', tick);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', tick);
      window.removeEventListener('allocchange:poke', tick);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [tick]);

  if (!pop) return null;

  async function ackAll() {
    if (!pop) return;
    setBusy(true);
    try {
      await api.ackAllocChanges({ ids: pop.map((r) => r.alloc_change_id) });
      setPop(null);
      tick();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal width="max-w-3xl">
      <AllocAlertCard
        pop={pop}
        busy={busy}
        sales={sales}
        onGo={(mon) => { setPop(null); navigate(`/alloc-changes?mon=${mon}`); }}
        onLater={() => { snooze(); setPop(null); }}
        onAck={() => void ackAll()}
      />
    </Modal>
  );
}

function AllocAlertCard({ pop, busy, sales, onGo, onLater, onAck }: {
  pop: AllocChangeRow[]; busy: boolean; sales: boolean; onGo: (mon: string) => void; onLater: () => void; onAck: () => void;
}) {
  const mons = [...new Set(pop.map((r) => r.settle_mon))].sort();
  return (
    <div className="card space-y-3 p-5">
      <div>
        <div className="text-lg font-bold">🚌 운행편성이 바뀌었습니다 ({pop.length}건)</div>
        <p className="mt-1 text-sm text-ink-soft">
          {sales
            ? '차량·기사가 바뀌거나 끝난 편성입니다. 아래 「차량 없는 노선」은 종료일 뒤로 배차가 비어 있으니 다시 배차해 주세요.'
            : `${mons.join('·')} 정산에서 차량·차주·기사 조합이 바뀌거나 끝난 편성입니다. 정산할 때 옛 편성과 새 편성을 나눠 확인해 주세요.`}
        </p>
      </div>
      <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
        {pop.map((r) => <AllocAlertItem key={r.alloc_change_id} r={r} sales={sales} />)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-ghost" onClick={() => onGo(mons[mons.length - 1]!)}>편성 변경 화면에서 보기</button>
        <button className="btn-ghost ml-auto" onClick={onLater}>나중에</button>
        <button className="btn-primary" disabled={busy} onClick={onAck}>{busy ? '저장 중…' : '확인했습니다'}</button>
      </div>
    </div>
  );
}

/** 편성 한 칸 — 차량번호를 크게, 아래에 기사·차주·소속(스페이스 카드와 같은 순서). */
function SnapBox({ label, s, tone }: { label: string; s: AllocSnap | null | undefined; tone: string }) {
  return (
    <div className={`min-w-0 flex-1 rounded-lg border px-3 py-2 ${tone}`}>
      <div className="text-xs text-ink-soft">{label}{s ? ` · 편성 NO ${s.car_alloc_id}` : ''}</div>
      <div className="truncate text-base font-bold">{s?.car_no || (s ? `편성 ${s.car_alloc_id}` : '—')}</div>
      <div className="text-xs text-ink-soft">
        {[s?.driver_name ? `기사 ${s.driver_name}` : null, s?.owner_name ? `차주 ${s.owner_name}` : null, s?.grp_name].filter(Boolean).join(' · ')}
      </div>
    </div>
  );
}

/**
 * 알림 한 건 — 바뀐 편성(이전 → 변경 후)과 **비고**를 눈에 띄게(10-12 way 「모달로 잘 보이도록 · 비고 포함」).
 * 비고 = 등록한 사람이 쓴 글(굵게 · 노란 상자) · 시스템이 붙인 [자동] 줄은 아래 작은 글씨로 나눈다.
 */
export function AllocAlertItem({ r, sales }: { r: AllocChangeRow; sales: boolean }) {
  const lines = (r.comment ?? '').split('\n').map((x) => x.trim()).filter(Boolean);
  const memo = lines.filter((x) => !x.startsWith('[자동]') && !x.startsWith('종료일 뒤 차량 없는 노선'));
  const open = lines.filter((x) => x.startsWith('종료일 뒤 차량 없는 노선'));
  const auto = lines.filter((x) => x.startsWith('[자동]')).map((x) => x.replace(/^\[자동\]\s*/, ''));
  return (
    <div className="space-y-2 rounded-xl border border-line p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <KindBadge kind={r.kind} />
        <span className="font-semibold">{r.reason}</span>
        <span className="rounded bg-canvas px-1.5 py-0.5 text-xs">{changeWhen(r)}</span>
        {!sales && <span className="rounded bg-canvas px-1.5 py-0.5 text-xs">{r.settle_mon} 정산</span>}
        <span className="ml-auto text-xs text-ink-faint">NO {r.change_no} · {r.created_name} · {r.created_at}</span>
      </div>
      {r.kind === '변경' ? (
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <SnapBox label="이전 편성" s={r.old_snap} tone="border-line bg-canvas" />
          <span className="self-center text-lg text-ink-faint">→</span>
          <SnapBox label="변경 후 편성" s={r.new_snap} tone="border-amber-300 bg-amber-50" />
        </div>
      ) : (
        <SnapBox label={r.kind === '신규' ? '새 편성' : r.kind === '삭제' ? '삭제한 편성' : '종료한 편성'}
          s={r.kind === '신규' ? r.new_snap : (r.old_snap ?? r.new_snap)} tone={r.kind === '신규' ? 'border-emerald-300 bg-emerald-50' : 'border-red-200 bg-red-50'} />
      )}
      {(memo.length > 0 || !!r.files?.length) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
          <div className="text-xs font-semibold text-amber-800">비고</div>
          {memo.length > 0 && <div className="whitespace-pre-line text-sm font-medium text-ink">{memo.join('\n')}</div>}
          {!!r.files?.length && <AllocFileChips files={r.files} className="mt-1.5" />}
        </div>
      )}
      {open.map((x) => <div key={x} className="text-sm font-medium text-red-700">{x}</div>)}
      {auto.length > 0 && <div className="whitespace-pre-line text-xs text-ink-faint">{auto.join('\n')}</div>}
    </div>
  );
}

const sizeText = (b: string | number) => {
  const n = Number(b);
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`;
};

/** 편성 변경 첨부(#189) — 누르면 새 창으로 연다(이미지·PDF) · 그 밖은 내려받기. `onRemove` 가 있으면 × 로 내린다. */
export function AllocFileChips({ files, onRemove, className = '', fetchUrl = fetchAllocChangeFileUrl }: {
  files: { id: number; file_name: string; content_type: string; byte_size: string | number }[];
  onRemove?: (id: number) => void;
  className?: string;
  /** 첨부 받는 길 — 기본 = 편성 변경 첨부. 할부 상환스케줄표(#230)도 같은 칩을 쓴다. */
  fetchUrl?: (id: number) => Promise<string>;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  async function open(id: number) {
    setBusy(id);
    try {
      const url = await fetchUrl(id);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  }
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {files.map((f) => {
        const icon = f.content_type.startsWith('image/') ? '🖼' : f.content_type === 'application/pdf' ? '📕' : '📄';
        return (
          <span key={f.id} className="inline-flex items-center gap-1 rounded-lg border border-line bg-white px-2 py-1 text-xs">
            <button type="button" className="max-w-[16rem] truncate text-left hover:underline" disabled={busy === f.id}
                    onClick={(e) => { e.stopPropagation(); void open(f.id); }}>{icon} {f.file_name}</button>
            <span className="text-ink-faint">{sizeText(f.byte_size)}</span>
            {onRemove && <button type="button" className="text-ink-faint hover:text-red-600" title="첨부 내리기" onClick={() => onRemove(f.id)}>×</button>}
          </span>
        );
      })}
    </div>
  );
}

/**
 * 정산 화면 위 강조 띠 — 그 정산월에 편성 변경이 있으면 늘 보인다(확인한 뒤에도 접힌 한 줄로 남는다).
 * 「같은 차량인데 옛 편성·새 편성이 섞여 있다」를 정산하는 순간에 눈앞에 두는 것이 목적이다.
 */
export function AllocChangeBanner({ mon, compact = false }: { mon: string; compact?: boolean }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AllocChangeRow[] | null>(null);
  const [open, setOpen] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(mon)) { setRows(null); return; }
    let cancelled = false;
    api.listAllocChanges({ mon, notifyOnly: true, limit: 100 })
      .then((r) => { if (!cancelled) setRows(r.rows); })
      .catch(() => { if (!cancelled) setRows(null); });
    return () => { cancelled = true; };
  }, [mon, nonce]);

  if (!rows || !rows.length) return null;
  const unacked = rows.filter((r) => !r.acked);

  async function ack(ids: number[]) {
    await api.ackAllocChanges({ ids });
    setNonce((n) => n + 1);
    pokeAllocChanges();
  }

  const tone = unacked.length ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-line bg-canvas text-ink-soft';
  return (
    // compact = 조회줄 안에 끼는 한 칸(#415 월정기 · 10-27 way 「상단 최소화」) — 펼치면 줄 하나를 다 쓴다.
    <div className={compact
      ? `rounded-lg border px-2 py-0.5 text-xs ${tone} ${open ? 'basis-full' : ''}`
      : `rounded-lg border px-3 py-1 text-sm ${tone}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold" title="같은 차량이라도 옛 편성·새 편성 줄을 나눠 확인하세요.">
          🚌 {compact ? '' : `${mon} 운행`}편성 변경 {rows.length}건{unacked.length ? ` · 확인 안 함 ${unacked.length}` : ''}
        </span>
        {!compact && <span className="hidden text-xs 2xl:inline">같은 차량이라도 옛 편성·새 편성 줄을 나눠 확인하세요.</span>}
        <button className="ml-auto text-xs underline" onClick={() => setOpen((v) => !v)}>{open ? '접기' : '펼치기'}</button>
        {unacked.length > 0 && (
          <button className="btn-ghost py-0.5 text-xs" onClick={() => void ack(unacked.map((r) => r.alloc_change_id))}>모두 확인</button>
        )}
        <button className="text-xs underline" onClick={() => navigate(`/alloc-changes?mon=${mon}`)}>편성 변경 보기</button>
      </div>
      {open && (
        <ul className="mt-2 divide-y divide-amber-200">
          {rows.map((r) => (
            <li key={r.alloc_change_id} className="flex flex-wrap items-center gap-2 py-1">
              <span className="font-mono text-xs text-ink-soft">{r.change_no}</span>
              <KindBadge kind={r.kind} />
              <span className="text-xs">{changeWhen(r)}</span>
              <span className="min-w-0 flex-1 truncate" title={changeText(r)}>{changeText(r)}</span>
              {r.acked ? <span className="text-xs text-ink-faint">확인함</span>
                : <button className="btn-ghost py-0.5 text-xs" onClick={() => void ack([r.alloc_change_id])}>확인</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
